import React, { useEffect, useRef } from 'react';
import { BookOpen, ExternalLink, FileText } from 'lucide-react';
import { realPublications, manuscriptsInPreparation } from '../data/publications';
import { Card, CardHeader, CardTitle } from './ui/card';

const Publications = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('active');
              }, index * 50);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const featuredPubs = realPublications.filter(p => p.featured);
  const otherPubs = realPublications.filter(p => !p.featured);

  return (
    <section ref={sectionRef} id="publications" className="py-28 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-indigo-200 mb-8 shadow-lg shadow-indigo-100/50">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            <span className="text-sm font-bold text-indigo-700 tracking-wide">PUBLICATIONS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
            Research <span className="gradient-text">Publications</span>
          </h2>
          <div className="w-40 h-2 bg-gradient-to-r from-indigo-600 via-cyan-500 to-amber-500 mx-auto rounded-full shadow-lg"></div>
        </div>

        {/* Manuscripts in Preparation */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="h-6 w-6 text-amber-600" />
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Manuscripts in Preparation
            </h3>
          </div>
          <div className="space-y-4">
            {manuscriptsInPreparation.map((manuscript, index) => (
              <Card 
                key={manuscript.id}
                className="reveal hover-lift glass-light border-l-4 border-l-amber-500"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader className="py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-lg md:text-xl text-slate-900 mb-3 leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {manuscript.title}
                      </CardTitle>
                      <div className="flex flex-wrap items-center gap-3 text-base">
                        <span className="text-slate-600">{manuscript.authors}</span>
                        <span className="text-slate-400">•</span>
                        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold text-sm capitalize">{manuscript.status}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Featured Publications */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Featured Publications
          </h3>
          <div className="space-y-6">
            {featuredPubs.map((pub, index) => (
              <Card 
                key={pub.id}
                className="reveal hover-lift glass-light border-l-4 border-l-indigo-500"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-lg md:text-xl text-slate-900 mb-3 leading-snug hover:text-indigo-600 transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {pub.title}
                      </CardTitle>
                      <p className="text-base text-slate-600 mb-3">{pub.authors}</p>
                      <div className="flex flex-wrap items-center gap-3 text-base">
                        <span className="font-bold text-indigo-600">{pub.journal}</span>
                        <span className="text-slate-500">•</span>
                        <span className="text-slate-600">{pub.year}</span>
                        <span className="text-slate-500">•</span>
                        <span className="text-slate-600">PMID: {pub.pmid}</span>
                        {pub.note && (
                          <>
                            <span className="text-slate-500">•</span>
                            <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold text-sm">{pub.note}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <a 
                      href={`https://pubmed.ncbi.nlm.nih.gov/${pub.pmid}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center gap-1 flex-shrink-0"
                    >
                      View
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Links to profiles */}
        <div className="mt-16 text-center reveal">
          <p className="text-base md:text-lg text-slate-600 mb-6">View complete publication profiles:</p>
          <div className="flex justify-center gap-4">
            <a 
              href="https://scholar.google.com/citations?hl=en&user=RQ_3xm4AAAAJ"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 premium-gradient text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/50 transition-all duration-300"
            >
              Google Scholar
            </a>
            <a 
              href="https://orcid.org/0000-0002-9175-8110"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 glass-light border border-indigo-200 text-indigo-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
            >
              ORCID
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Publications;